import L from 'leaflet';

// "Kontext + fokus" for the Linjer (lines-only) Färger mode: the area outlines are drawn faint everywhere (so the
// whole structure is still readable — done in Map.js's setBaseStyle via a lower stroke opacity,
// LINJER_BASE_OPACITY), and this overlay redraws the outlines at full strength inside a circle around the mouse, so
// the lines "light up" where you point. On a touch screen they light up round the finger while it pans the map (not
// while it's only held down, nor during a pinch) and fade once it lets go (the owner, 2026-11-02). It reads the
// areas' already-projected pixel rings (`layer._parts`) and only redraws the few areas whose bounds fall within the
// circle, so each move is cheap. Clicked / selected / hovered areas keep their full highlight through their own
// styling (they aren't fainted) and the existing panes (grey covers, selection line) are untouched.

const RADIUS = 150;
// The outlines' opacity away from the mouse (Map.js setBaseStyle; selected areas stay at full strength). Faint
// enough that the circle around the mouse still lights up, strong enough that the whole structure reads.
export const LINJER_BASE_OPACITY = 0.45;
const Z_INDEX = 404; // above the area lines (overlay pane 400), below the funnel badges (405) and labels (440)
// The lit lines are drawn thicker than the 0.75px base, over an edge. Redrawn at the base width a thin coloured
// line barely changed (on the busy, light Bright map least of all: roads, parks and water are close to some score
// colours); the edge sets each line apart from what's under it: white on the light map, dark on the dark one
// (white glared there; dark also parts the lines from its lighter grey roads).
const FOCUS_WIDTH = 1.85;
const FOCUS_EDGE = 1; // on each side
const FOCUS_EDGE_COLOUR = { light: 'rgba(255, 255, 255, 0.92)', dark: 'rgba(20, 24, 23, 0.85)' };
// The hovered / opened area's outline (3px on the area canvas) is redrawn over the lit lines this much wider than
// them, with the same edge, so it stays the boldest line in the circle.
const FOCUS_TOP_EXTRA = 1.5;
// A finger lights the lines once it has moved this far (a pan; a finger held still is a long press).
const TOUCH_PAN_SLOP = 8;
// After the finger lets go the light fades out over this long.
const TOUCH_FADE_MS = 320;

const matches = (query) => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia(query).matches;
export const isDesktopPointer = () => matches('(hover: hover) and (pointer: fine)');
// A mouse, or a touch screen (a finger lights the lines while it pans).
export const hasLinjerFocus = () => isDesktopPointer() || matches('(any-pointer: coarse)');

// topOutlines(): the hovered / opened areas' own outline layers (Map.js showOutline), redrawn over the lit lines.
export const createLinjerFocus = (map, { topOutlines = () => [] } = {}) => {
    const pane = map.getPane('linjerFocusPane') || map.createPane('linjerFocusPane');
    pane.style.zIndex = String(Z_INDEX);
    pane.style.pointerEvents = 'none';
    const canvas = document.createElement('canvas');
    canvas.style.position = 'absolute';
    pane.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    const container = map.getContainer();

    let active = false;
    let layerGroup = null;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let offset = L.point(0, 0);          // layer point -> container point (the map pane's offset)
    let cache = [];                       // [{ path, minX, minY, maxX, maxY, color, weight }] in layer coords
    let mouse = null;                     // container point (a mouse, or a panning finger)
    let moving = false;
    let zooming = false;
    // A finger on the map: its start, and whether it has moved far enough to be a pan (then it lights the lines,
    // also while the map moves under it).
    const touches = new Map();            // pointerId -> { x, y } at pointerdown
    let fingerPanning = false;
    let fadeTimer = null;
    let rafId = null;
    // Whether the canvas is known to be blank. While the map moves, draw() runs on every mouse move of a drag
    // and used to clear the whole (already cleared) canvas each time; a blank canvas is left be.
    let blank = false;

    const follow = () => {
        offset = map.layerPointToContainerPoint(L.point(0, 0)); // = the map pane's current offset
        L.DomUtil.setPosition(canvas, offset.multiplyBy(-1));   // so the canvas's (0,0) sits at the viewport corner
    };

    const reposition = () => {
        dpr = Math.min(window.devicePixelRatio || 1, 2);
        follow();
        const size = map.getSize();
        // Sizing a canvas clears it (and reallocates it, even at the same size): at the same size it's only
        // cleared, which is all that changed for what's shown.
        const width = Math.max(1, Math.round(size.x * dpr));
        const height = Math.max(1, Math.round(size.y * dpr));
        if (canvas.width !== width || canvas.height !== height) {
            canvas.width = width;
            canvas.height = height;
            blank = true;
        } else {
            clear();
        }
        canvas.style.width = `${size.x}px`;
        canvas.style.height = `${size.y}px`;
    };

    // An outline's rings as one path, in layer coords.
    const ringsPath = (parts) => {
        const path = new Path2D();
        parts.forEach((ring) => {
            ring.forEach((p, i) => {
                if (i) path.lineTo(p.x, p.y); else path.moveTo(p.x, p.y);
            });
            path.closePath();
        });
        return path;
    };

    // Cache each area's outline as a path and its bounding box (layer coords, which a pan doesn't change), so a move
    // only has to cull and stroke the few areas under the circle, shifted by the map pane's offset. Rebuilt whenever
    // the projection changes (move/zoom/restyle).
    const refresh = () => {
        cache = [];
        if (!active || !layerGroup) return;
        layerGroup.eachLayer((layer) => {
            const parts = layer._parts;
            if (!parts || !parts.length || layer.options.stroke === false) return;
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            parts.forEach((ring) => ring.forEach((p) => {
                const { x, y } = p;
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
            }));
            cache.push({ path: ringsPath(parts), minX, minY, maxX, maxY, color: layer.options.color, weight: layer.options.weight || 0.75 });
        });
    };

    const clear = () => {
        if (blank) return;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        blank = true;
    };

    const draw = () => {
        rafId = null;
        // A panning finger draws while the map moves: the canvas is moved back over the view first (it sits in the
        // map pane, which the pan shifts).
        if (fingerPanning && moving && !zooming) follow();
        clear();
        if (!active || !mouse || zooming || (moving && !fingerPanning)) return;
        // Turned on before the areas were drawn (the first load), the cache is empty until the map moves: fill it now.
        if (!cache.length) refresh();
        blank = false;
        const ox = offset.x, oy = offset.y;
        // The outlines are in layer coords: drawn shifted by the pane's offset.
        ctx.setTransform(dpr, 0, 0, dpr, ox * dpr, oy * dpr);
        ctx.globalCompositeOperation = 'source-over';
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        const left = mouse.x - RADIUS, right = mouse.x + RADIUS, top = mouse.y - RADIUS, bottom = mouse.y + RADIUS;
        const lit = [];
        for (const a of cache) {
            if (a.maxX + ox < left || a.minX + ox > right || a.maxY + oy < top || a.minY + oy > bottom) continue;
            lit.push({ path: a.path, a });
        }
        if (!lit.length) return;
        const edgeColour = document.body.classList.contains('theme-dark') ? FOCUS_EDGE_COLOUR.dark : FOCUS_EDGE_COLOUR.light;
        // Every edge first, then every line, so one area's edge never covers its neighbour's line.
        ctx.strokeStyle = edgeColour;
        lit.forEach(({ path, a }) => {
            ctx.lineWidth = Math.max(a.weight, FOCUS_WIDTH) + (FOCUS_EDGE * 2);
            ctx.stroke(path);
        });
        lit.forEach(({ path, a }) => {
            ctx.strokeStyle = a.color;
            ctx.lineWidth = Math.max(a.weight, FOCUS_WIDTH);
            ctx.stroke(path);
        });
        // The hovered / opened areas' thick outlines sit on the area canvas below this one, so the lit lines
        // and their edges would cover them: drawn again on top, wider than the lit lines and with the same
        // edge, they stay the boldest lines here.
        topOutlines().forEach((layer) => {
            const parts = layer._map && layer._parts;
            if (!parts || !parts.length || layer.options.stroke === false) return;
            const path = ringsPath(parts);
            const width = Math.max(layer.options.weight || 1, FOCUS_WIDTH + FOCUS_TOP_EXTRA);
            ctx.globalAlpha = layer.options.opacity ?? 1;
            ctx.strokeStyle = edgeColour;
            ctx.lineWidth = width + (FOCUS_EDGE * 2);
            ctx.stroke(path);
            ctx.strokeStyle = layer.options.color;
            ctx.lineWidth = width;
            ctx.stroke(path);
            ctx.globalAlpha = 1;
        });
        // Feather the lit lines into a soft circle around the cursor (full to ~half the radius, fading to the
        // edge), so the focus blends into the faint base instead of ending in a hard ring.
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        const g = ctx.createRadialGradient(mouse.x, mouse.y, RADIUS * 0.5, mouse.x, mouse.y, RADIUS);
        g.addColorStop(0, 'rgba(0,0,0,1)');
        g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.globalCompositeOperation = 'destination-in';
        ctx.fillStyle = g;
        ctx.fillRect(left, top, RADIUS * 2, RADIUS * 2);
        ctx.globalCompositeOperation = 'source-over';
    };
    const queue = () => { if (!rafId) rafId = requestAnimationFrame(draw); };

    const containerPoint = (event) => {
        const rect = container.getBoundingClientRect();
        return L.point(event.clientX - rect.left, event.clientY - rect.top);
    };
    const onMove = (event) => {
        if (!active || event.pointerType === 'touch') return;
        mouse = containerPoint(event);
        queue();
    };
    const onLeave = (event) => {
        if (event.pointerType === 'touch') return;
        mouse = null;
        queue();
    };

    // A finger: it lights the lines once it pans, fades when it lets go; a second finger (a pinch) puts it out.
    const stopFade = () => {
        if (fadeTimer) clearTimeout(fadeTimer);
        fadeTimer = null;
        canvas.style.transition = '';
        canvas.style.opacity = '';
    };
    const fadeOut = () => {
        fingerPanning = false;
        if (!mouse) return;
        if (matches('(prefers-reduced-motion: reduce)')) {
            mouse = null;
            queue();
            return;
        }
        canvas.style.transition = `opacity ${TOUCH_FADE_MS}ms ease`;
        canvas.style.opacity = '0';
        fadeTimer = setTimeout(() => {
            fadeTimer = null;
            mouse = null;
            clear();
            stopFade();
        }, TOUCH_FADE_MS);
    };
    const onTouchDown = (event) => {
        if (event.pointerType !== 'touch') return;
        touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (touches.size > 1 && fingerPanning) fadeOut();
    };
    const onTouchMove = (event) => {
        if (event.pointerType !== 'touch' || !active || touches.size !== 1) return;
        const start = touches.get(event.pointerId);
        if (!start) return;
        if (!fingerPanning) {
            if (Math.hypot(event.clientX - start.x, event.clientY - start.y) < TOUCH_PAN_SLOP) return;
            fingerPanning = true;
            stopFade();
        }
        mouse = containerPoint(event);
        queue();
    };
    const onTouchUp = (event) => {
        if (event.pointerType !== 'touch' || !touches.delete(event.pointerId)) return;
        if (fingerPanning) fadeOut();
    };
    // Leaflet rebuilds the outlines (`layer._parts`: clipped, at the new zoom) in the area renderer's own
    // moveend handler, which runs after this one (it was registered later). Re-reading them here cached the
    // previous zoom's outlines, and the focus drew those as ghost lines where no area edge is. So the cache is
    // refreshed on the next frame, once the renderer has updated.
    let refreshRafId = null;
    const refreshSoon = () => {
        if (refreshRafId) cancelAnimationFrame(refreshRafId);
        refreshRafId = requestAnimationFrame(() => {
            refreshRafId = null;
            reposition();
            refresh();
            queue();
        });
    };
    const onMoveStart = () => {
        moving = true;
        if (refreshRafId) cancelAnimationFrame(refreshRafId);
        refreshRafId = null;
        if (!fingerPanning) clear();
    };
    const onZoomStart = () => {
        zooming = true;
        clear();
    };
    const onMoveEnd = () => { moving = false; refreshSoon(); };
    const onZoomEnd = () => { zooming = false; };
    const onResize = () => { reposition(); refresh(); queue(); };

    container.addEventListener('pointermove', onMove);
    container.addEventListener('pointerleave', onLeave);
    // Capture phase on the container (the map's own handlers may stop a finger's events further in); the moves and
    // the lift on the window, where they still arrive once the finger has left the container.
    container.addEventListener('pointerdown', onTouchDown, true);
    window.addEventListener('pointermove', onTouchMove, true);
    window.addEventListener('pointerup', onTouchUp, true);
    window.addEventListener('pointercancel', onTouchUp, true);
    map.on('movestart', onMoveStart);
    map.on('zoomstart', onZoomStart);
    map.on('moveend zoomend', onMoveEnd);
    map.on('zoomend', onZoomEnd);
    map.on('resize', onResize);
    reposition();

    return {
        // on: whether Linjer mode is on; group: the RegSO layer group. With a mouse or a finger (hasLinjerFocus).
        setActive(on, group) {
            if (group) layerGroup = group;
            const next = Boolean(on) && hasLinjerFocus();
            active = next;
            if (active) { reposition(); refresh(); queue(); }
            else { mouse = null; fingerPanning = false; stopFade(); clear(); }
            return active;
        },
        // After a restyle (colours/geometry changed) while active.
        refresh() { if (active) { reposition(); refresh(); queue(); } },
        // Draw again with the same outlines (a hovered area's outline came or went).
        redraw() { if (active) queue(); },
        isActive() { return active; },
        remove() {
            container.removeEventListener('pointermove', onMove);
            container.removeEventListener('pointerleave', onLeave);
            container.removeEventListener('pointerdown', onTouchDown, true);
            window.removeEventListener('pointermove', onTouchMove, true);
            window.removeEventListener('pointerup', onTouchUp, true);
            window.removeEventListener('pointercancel', onTouchUp, true);
            map.off('movestart', onMoveStart);
            map.off('zoomstart', onZoomStart);
            map.off('moveend zoomend', onMoveEnd);
            map.off('zoomend', onZoomEnd);
            map.off('resize', onResize);
            if (rafId) cancelAnimationFrame(rafId);
            if (refreshRafId) cancelAnimationFrame(refreshRafId);
            if (fadeTimer) clearTimeout(fadeTimer);
            canvas.remove();
        },
    };
};
